import {
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useCallback, useEffect, useState } from 'react';

import { Targets, type TargetType } from '@atlassiansox/cross-flow-support/cross-flow-base-types';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

// This is adding interaction between placement and the app (in our case, it is Atlas/Townsquare)
type AppProps = {
	onGrowthItemClick?: (targetProduct: TargetType) => void; // This is the callback that will be called when the growth item is clicked.
	onGrowthItemViewed?: (targetProduct: TargetType) => void; // This is the callback that will be called when the growth item is viewed.
};

type CustomDismissCallback = () => void;

// This type will be the contract between the message and the placement
export type PlacementRenderProps = {
	dismiss: CustomDismissCallback;
} & AppProps;

// We use the CustomRenderer type to tell the placement that these props are
// going to be used for renderering.
type placementDefinition = CustomRenderer<PlacementRenderProps>;

/**
 * This placement creation is following the guide here:
 * https://developer.atlassian.com/platform/post-office/working-with-post-office/guides/in-app-v2/customize-your-placement/
 */
const placementConfig: PlacementBrowserConfig = {
	placementId: 'atlas-side-nav-growth-item',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
		hideInIsolatedCloud: true,
	},
};

const placement = {
	createFrontend: createPlacementFrontend<placementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => ({ onGrowthItemClick, onGrowthItemViewed }) => {
	// We make a client message state so we can track which message are mounted,
	// and dismissal state to abort display of messages if has dismissed.
	const [messages, setMessages] = useState<
		DefaultMessagesResponse<{ targetProduct: TargetType }>['messages']
	>([]);

	const [isDismissed, setDismissed] = useState(false);

	const handleDismiss = useCallback(() => {
		setDismissed(true);
	}, [setDismissed]);

	const apiResponse = usePlacementApi<DefaultMessagesResponse<{ targetProduct: TargetType }>>('/');

	useEffect(() => {
		// We add an effect to update the messages state when there is a new api response
		const { error, data, isLoading } = apiResponse;

		if (isLoading) {
			return;
		}

		if (error) {
			throw error;
		}

		const messageTargetTypeOrder: TargetType[] = [
			Targets.JIRA_PRODUCT_DISCOVERY,
			Targets.JIRA_SOFTWARE,
			Targets.CONFLUENCE,
			Targets.JIRA_SERVICE_DESK,
			Targets.COMPASS,
		];

		const sortedMessages = data.messages.sort((a, b) => {
			const aIndex = messageTargetTypeOrder.indexOf(a.targetProduct);
			const bIndex = messageTargetTypeOrder.indexOf(b.targetProduct);

			if (aIndex !== -1 && bIndex !== -1) {
				return aIndex - bIndex;
			}
			// If only a is in the priority list, it comes first
			if (aIndex !== -1) {
				return -1;
			}
			// If only b is in the priority list, it comes first
			if (bIndex !== -1) {
				return 1;
			}
			// If neither is in the priority list, maintain original order
			return 0;
		});

		setMessages(sortedMessages);
	}, [apiResponse]);

	if (messages.length === 0 || isDismissed) {
		return null;
	}

	const firstMessage = messages[0];
	if (!firstMessage) {
		return null;
	}

	return (
		<Messages.render
			key={firstMessage.messageInstanceId}
			dismiss={handleDismiss}
			onGrowthItemClick={onGrowthItemClick}
			onGrowthItemViewed={onGrowthItemViewed}
			{...firstMessage}
		/>
	);
});

export type PlacementFrontendProps = {
	onGrowthItemClick?: (targetProduct: TargetType) => void;
	onGrowthItemViewed?: (targetProduct: TargetType) => void;
};
